import React, { useRef, useEffect, useState, useCallback } from "react";
import {
	createClient,
	createCameraVideoTrack,
	createMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
	Stack,
	Box,
	Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CameraFrontIcon from "@mui/icons-material/CameraFront";
import CameraRearIcon from "@mui/icons-material/CameraRear";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { useSelector } from "react-redux";
import { getToken, analyseImage } from "./utility/api";

const AgoraVideoCallWithSnapshot = ({ onCallEnd, handleLossSubmit, participantRole }) => {
	const localTrackRef = useRef(null);
	const localAudioTrackRef = useRef(null);
	const client = useRef(null);
	const loggedInUser = useSelector((state) => state.user.user);
	const canvasRef = useRef(null);
	const [snapshots, setSnapshots] = useState([]);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [callEnded, setCallEnded] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [isUsingBackCamera, setIsUsingBackCamera] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const [initError, setInitError] = useState(null);
	const [isSwapped, setIsSwapped] = useState(false);
	const remoteTrackRef = useRef(null);
	const mainVideoRef = useRef(null);
	const pipVideoRef = useRef(null);
	const isSwappedRef = useRef(false);
	const appId = process.env.APP_ID || "ed95578789324a9ca3b81eb8b2b98514";
	const currentAssignment = useSelector(
		(state) => state.assignment.assignment
	);
	const user = useSelector((state) => state.user.user);

	// Refs for latest values used inside the one-shot init effect
	const currentAssignmentRef = useRef(currentAssignment);
	const userRef = useRef(user);
	const participantRoleRef = useRef(participantRole);
	const appIdRef = useRef(appId);
	useEffect(() => {
		currentAssignmentRef.current = currentAssignment;
		userRef.current = user;
		participantRoleRef.current = participantRole;
		appIdRef.current = appId;
	}, [currentAssignment, user, participantRole, appId]);

	useEffect(() => {
		isSwappedRef.current = isSwapped;
	}, [isSwapped]);

	const isUsingBackCameraRef = useRef(isUsingBackCamera);
	useEffect(() => {
		isUsingBackCameraRef.current = isUsingBackCamera;
	}, [isUsingBackCamera]);

	const playLocalIntoCurrentContainer = useCallback(() => {
		if (!localTrackRef.current) return;
		const container = isSwappedRef.current ? mainVideoRef.current : pipVideoRef.current;
		if (!container) return;
		// Remove any stale <video> left behind by a previous track.play() so the SDK
		// can attach a fresh element instead of stacking on top of an orphan.
		while (container.firstChild) container.removeChild(container.firstChild);
		try {
			localTrackRef.current.play(container, {
				fit: "cover",
				mirror: !isUsingBackCameraRef.current,
			});
		} catch (err) {
			console.error("Failed to play local track:", err);
		}
	}, []);

	const playRemoteIntoCurrentContainer = useCallback(() => {
		if (!remoteTrackRef.current) return;
		const container = isSwappedRef.current ? pipVideoRef.current : mainVideoRef.current;
		if (!container) return;
		while (container.firstChild) container.removeChild(container.firstChild);
		try {
			remoteTrackRef.current.play(container, { fit: "contain", mirror: false });
		} catch (err) {
			console.error("Failed to play remote track:", err);
		}
	}, []);

	useEffect(() => {
		const userAgent = navigator.userAgent || window.opera;
		if (/android|iPad|iPhone|iPod/i.test(userAgent)) {
			setIsMobile(true);
		}
	}, []);

	// One-shot init: runs once per call session, never on swap/camera/role change
	useEffect(() => {
		if (callEnded) return undefined;

		let cancelled = false;

		const initAgora = async () => {
			setInitError(null);

			const assignment = currentAssignmentRef.current;
			if (!assignment?.assignmentName) {
				setInitError("No assignment selected. Open a claim and try again.");
				return;
			}

			const effectiveRole = participantRoleRef.current || userRef.current?.role || "Claimant";

			try {
				client.current = createClient({ mode: "rtc", codec: "vp8" });

				// Register handlers BEFORE join to avoid missing the publish event
				client.current.on("user-published", async (remoteUser, mediaType) => {
					try {
						if (mediaType === "video") {
							const remoteTrack = await client.current.subscribe(remoteUser, "video");
							remoteTrackRef.current = remoteTrack;
							playRemoteIntoCurrentContainer();
						}
						if (mediaType === "audio") {
							const remoteAudioTrack = await client.current.subscribe(remoteUser, "audio");
							remoteAudioTrack.play();
						}
					} catch (err) {
						console.error("Failed subscribing to remote user:", err);
					}
				});

				client.current.on("user-unpublished", (_remoteUser, mediaType) => {
					if (mediaType === "video") {
						remoteTrackRef.current = null;
					}
				});

				client.current.on("user-left", () => {
					remoteTrackRef.current = null;
				});

				const token = await getToken(assignment.assignmentName, 0, effectiveRole);

				await client.current.join(appIdRef.current, assignment.assignmentName, token, 0);
				if (cancelled) return;

				localTrackRef.current = await createCameraVideoTrack({
					facingMode: isUsingBackCamera ? "environment" : "user",
				});
				if (cancelled) {
					localTrackRef.current?.close();
					localTrackRef.current = null;
					return;
				}

				localAudioTrackRef.current = await createMicrophoneAudioTrack();
				if (cancelled) {
					localAudioTrackRef.current?.close();
					localAudioTrackRef.current = null;
					return;
				}

				playLocalIntoCurrentContainer();

				await client.current.publish([
					localTrackRef.current,
					localAudioTrackRef.current,
				]);

				// Pick up any users who published before our handler was registered (shouldn't happen now, but safe)
				const remoteUsers = client.current.remoteUsers || [];
				for (const remoteUser of remoteUsers) {
					try {
						if (remoteUser.hasVideo) {
							const remoteTrack = await client.current.subscribe(remoteUser, "video");
							remoteTrackRef.current = remoteTrack;
							playRemoteIntoCurrentContainer();
						}
						if (remoteUser.hasAudio) {
							const remoteAudioTrack = await client.current.subscribe(remoteUser, "audio");
							remoteAudioTrack.play();
						}
					} catch (err) {
						console.error("Failed handling existing remote user:", err);
					}
				}
			} catch (err) {
				console.error("Failed to initialize Agora:", err);
				setInitError("Unable to start the video call. Please check camera/mic permissions and try again.");
			}
		};

		initAgora();

		return () => {
			cancelled = true;
			if (localTrackRef.current) {
				try { localTrackRef.current.stop(); localTrackRef.current.close(); } catch {}
				localTrackRef.current = null;
			}
			if (localAudioTrackRef.current) {
				try { localAudioTrackRef.current.stop(); localAudioTrackRef.current.close(); } catch {}
				localAudioTrackRef.current = null;
			}
			if (client.current) {
				client.current.removeAllListeners?.();
				client.current.leave().catch(() => {});
				client.current = null;
			}
			remoteTrackRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [callEnded]);

	// When swap toggles, just re-play tracks into their new containers (no reconnect)
	useEffect(() => {
		if (callEnded) return;
		// Remote first to minimize visible blank on main view
		playRemoteIntoCurrentContainer();
		playLocalIntoCurrentContainer();
	}, [isSwapped, callEnded, playLocalIntoCurrentContainer, playRemoteIntoCurrentContainer]);

	const swapVideos = () => {
		setIsSwapped((prev) => !prev);
	};

	const takeSnapshot = async () => {
		const canvas = canvasRef.current;
		const remoteContainer = isSwapped ? pipVideoRef.current : mainVideoRef.current;
		const video = remoteContainer?.querySelector('video');

		const notReady =
			!canvas ||
			!remoteContainer ||
			!(video instanceof HTMLVideoElement) ||
			video.readyState < 2 ||
			video.videoWidth === 0 ||
			video.videoHeight === 0;

		if (notReady) {
			const msg = "Remote video is not ready yet. Please wait a moment and try again.";
			setInitError(msg);
			setTimeout(() => {
				setInitError((prev) => (prev === msg ? null : prev));
			}, 3000);
			return;
		}

		const ctx = canvas.getContext("2d");
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

		const imageData = canvas.toDataURL("image/png");

		let customDescription = "",
			width = 1,
			height = 1,
			description = "",
			isSaved = false;
		try {
			const result = await analyseImage(imageData);

			width = parseFloat(result.width);
			height = parseFloat(result.height);
			description = result.description;
			customDescription = result.customDescription;
		} catch (error) {
			console.error("Error uploading data:", error);
		}

		setSnapshots((prevSnapshots) => [
			...prevSnapshots,
			{ imageData, customDescription, width, height, description, isSaved },
		]);
	};

	const endCall = async () => {
		try {
			if (localTrackRef.current) {
				localTrackRef.current.stop();
				localTrackRef.current.close();
				localTrackRef.current = null;
			}
			if (localAudioTrackRef.current) {
				localAudioTrackRef.current.stop();
				localAudioTrackRef.current.close();
				localAudioTrackRef.current = null;
			}

			if (client.current) {
				const localTracks = client.current.localTracks || [];
				localTracks.forEach((track) => {
					track.stop();
					track.close();
				});
				await client.current.leave();
				client.current = null;
			}
		} catch (err) {
			console.error("Error ending call:", err);
		}

		setCallEnded(true);
		if (onCallEnd) {
			onCallEnd(snapshots);
		}
	};

	const handleImageClick = (image) => {
		setSelectedImage(image);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
	};

	// In-place camera swap without leaving/rejoining the Agora channel
	const switchCamera = async () => {
		if (!client.current) return;
		const nextUseBack = !isUsingBackCamera;
		try {
			const newTrack = await createCameraVideoTrack({
				facingMode: nextUseBack ? "environment" : "user",
			});

			if (localTrackRef.current) {
				try { await client.current.unpublish(localTrackRef.current); } catch {}
				try { localTrackRef.current.stop(); localTrackRef.current.close(); } catch {}
			}

			localTrackRef.current = newTrack;
			await client.current.publish(newTrack);
			isUsingBackCameraRef.current = nextUseBack;
			setIsUsingBackCamera(nextUseBack);
			playLocalIntoCurrentContainer();
		} catch (err) {
			console.error("Failed to switch camera:", err);
		}
	};

	const openLossAssessmentForm = () => {
		if (!currentAssignment || !currentAssignment.assignmentName) {
			console.error(
				"Error: Assignment is undefined or missing assignmentName."
			);
			return;
		}

		const newTab = window.open(
			`/lossform/${currentAssignment.assignmentName}`,
			"_blank"
		);

		const handleMessage = (event) => {
			if (event.origin !== window.location.origin) {
				return;
			}

			const receivedData = event.data;

			if (
				!receivedData ||
				typeof receivedData !== "object" ||
				!receivedData.message
			) {
				console.error("Invalid data received:", receivedData);
				return;
			}

			if (typeof receivedData.message !== "object") {
				console.error(
					"Expected an object in message field:",
					receivedData.message
				);
				return;
			}

			const formData = new FormData();
			Object.entries(receivedData.message).forEach(([key, value]) => {
				formData.append(key, value);
			});

			handleLossSubmit(formData, snapshots);

			window.removeEventListener("message", handleMessage);
		};

		window.addEventListener("message", handleMessage);

		if (!newTab) {
			console.error("Failed to open new tab. Please allow pop-ups.");
		}
	};

	return (
		<Box
			sx={{
				position: "relative",
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				bgcolor: "#1a1a2e",
				borderRadius: 0,
				overflow: "hidden",
			}}
		>
			{initError && (
				<Typography color="error" sx={{ p: 2, textAlign: "center" }}>
					{initError}
				</Typography>
			)}
			{!callEnded && (
				<>
					{/* Main Video Area */}
					<Box
						sx={{
							position: "relative",
							width: "100%",
							flex: 1,
							minHeight: 0,
							bgcolor: "#0f0f23",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Box
							ref={mainVideoRef}
							sx={{
								width: "100%",
								height: "100%",
								"& video": {
									width: "100%",
									height: "100%",
									objectFit: "contain",
									borderRadius: "8px",
								},
							}}
						/>
						{/* Floating PiP Video - click to swap */}
						<Box
							onClick={swapVideos}
							sx={{
								position: "absolute",
								bottom: 16,
								right: 16,
								width: { xs: 90, sm: 120, md: 160 },
								height: { xs: 120, sm: 160, md: 220 },
								borderRadius: 2,
								overflow: "hidden",
								border: "2px solid rgba(255,255,255,0.3)",
								boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
								bgcolor: "#16213e",
								zIndex: 2,
								cursor: "pointer",
								"&:hover .swap-icon": { opacity: 1 },
							}}
						>
							<Box
								ref={pipVideoRef}
								sx={{
									width: "100%",
									height: "100%",
									"& video": {
										width: "100%",
										height: "100%",
										objectFit: "cover",
										borderRadius: "6px",
									},
								}}
							/>
							{/* Swap icon overlay on hover */}
							<Box
								className="swap-icon"
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									bgcolor: "rgba(0,0,0,0.4)",
									opacity: 0,
									transition: "opacity 0.2s",
								}}
							>
								<SwapVertIcon sx={{ color: "white", fontSize: 28 }} />
							</Box>
							<Typography
								variant="caption"
								sx={{
									position: "absolute",
									bottom: 4,
									left: 0,
									right: 0,
									textAlign: "center",
									color: "rgba(255,255,255,0.8)",
									fontSize: "0.65rem",
									textShadow: "0 1px 3px rgba(0,0,0,0.8)",
								}}
							>
								{isSwapped ? "Remote" : "You"}
							</Typography>
						</Box>
					</Box>

					<canvas
						ref={canvasRef}
						style={{ display: "none" }}
					/>

					{/* Controls Bar */}
					<Stack
						direction="row"
						useFlexGap
						sx={{
							flexWrap: "wrap",
							justifyContent: "center",
							alignItems: "center",
							rowGap: 1,
							columnGap: 1,
							px: 1.5,
							py: 1,
							bgcolor: "rgba(0,0,0,0.6)",
						}}
					>
						{(loggedInUser?.role === "Manager" ||
							loggedInUser?.role === "Adjuster") && (
							<Tooltip title="Take Snapshot">
								<IconButton
									onClick={() => takeSnapshot()}
									sx={{
										bgcolor: "primary.main",
										color: "white",
										width: { xs: 40, sm: 44 },
										height: { xs: 40, sm: 44 },
										"&:hover": { bgcolor: "primary.dark" },
									}}
								>
									<CameraAltIcon />
								</IconButton>
							</Tooltip>
						)}

						<Tooltip title="Swap View">
							<IconButton
								onClick={swapVideos}
								sx={{
									bgcolor: "grey.700",
									color: "white",
									width: { xs: 40, sm: 44 },
									height: { xs: 40, sm: 44 },
									"&:hover": { bgcolor: "grey.600" },
								}}
							>
								<SwapVertIcon />
							</IconButton>
						</Tooltip>

						<Tooltip title="End Call">
							<IconButton
								onClick={() => endCall()}
								sx={{
									bgcolor: "error.main",
									color: "white",
									width: { xs: 48, sm: 56 },
									height: { xs: 48, sm: 56 },
									"&:hover": { bgcolor: "error.dark" },
								}}
							>
								<CallEndIcon />
							</IconButton>
						</Tooltip>

						{isMobile && (
							<Tooltip title="Switch Camera">
								<IconButton
									onClick={() => switchCamera()}
									sx={{
										bgcolor: "grey.700",
										color: "white",
										width: { xs: 40, sm: 44 },
										height: { xs: 40, sm: 44 },
										"&:hover": { bgcolor: "grey.600" },
									}}
								>
									{isUsingBackCamera ? (
										<CameraFrontIcon />
									) : (
										<CameraRearIcon />
									)}
								</IconButton>
							</Tooltip>
						)}

						{snapshots && snapshots.length > 0 && (
							<Tooltip title="View Loss Assessment">
								<IconButton
									onClick={openLossAssessmentForm}
									sx={{
										bgcolor: "success.main",
										color: "white",
										width: { xs: 40, sm: 44 },
										height: { xs: 40, sm: 44 },
										"&:hover": { bgcolor: "success.dark" },
									}}
								>
									<AssessmentIcon />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</>
			)}

			{/* Captured Snapshots during call */}
			{!callEnded && snapshots.length > 0 && (
				<Box sx={{ p: 1.5, bgcolor: "rgba(0,0,0,0.4)", maxHeight: 180, overflowY: "auto" }}>
					<Typography variant="subtitle2" sx={{ color: "white", mb: 1 }}>
						Snapshots ({snapshots.length})
					</Typography>
					<Stack direction="row" spacing={1} sx={{ overflowX: "auto" }}>
						{snapshots.map((snapshot, index) => (
							<Box
								key={index}
								sx={{
									minWidth: 80,
									cursor: "pointer",
									borderRadius: 1,
									overflow: "hidden",
									border: "1px solid rgba(255,255,255,0.2)",
									"&:hover": { border: "1px solid rgba(255,255,255,0.6)" },
								}}
								onClick={() => handleImageClick(snapshot.imageData)}
							>
								<img
									src={snapshot.imageData}
									alt={`Snap ${index + 1}`}
									style={{
										width: 80,
										height: 60,
										objectFit: "cover",
									}}
								/>
							</Box>
						))}
					</Stack>
				</Box>
			)}

			{/* Dialog for displaying selected image */}
			<Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg">
				<DialogTitle>
					Snapshot
					<IconButton
						aria-label="close"
						onClick={handleCloseDialog}
						style={{ position: "absolute", right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent style={{ textAlign: "center" }}>
					{selectedImage && (
						<img
							src={selectedImage}
							alt="Selected Snapshot"
							style={{ width: "100%", height: "auto" }}
						/>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
};

export default AgoraVideoCallWithSnapshot;
